import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, Button, message, Modal, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import {
  formatMobileDisplayOrFallback,
  formatAadharDisplay,
  formatAadharDisplayOrFallback,
  sanitizeAadharInput,
  validateAadharNumber,
} from './formatDisplay';
import { isEatsAppRootAdmin } from '../utils/eatsAppAuth';
import './css/Profile.css';

function normalizeRoles(roles) {
  if (!roles) return [];
  const list = Array.isArray(roles) ? roles : [roles];
  const names = list.map((r) => {
    if (r == null) return '';
    if (typeof r === 'string') return r.trim();
    return String(r.roleName ?? r.name ?? r.Role_Name ?? r.role ?? '').trim();
  });
  return [...new Set(names.filter(Boolean))];
}

function normalizeUserProfile(raw, storedUser = null) {
  if (!raw || typeof raw !== 'object') return null;
  let roles = normalizeRoles(raw.roles);
  if (
    roles.length === 0 &&
    storedUser &&
    String(storedUser.domain || '').toUpperCase() === 'EATS_APP'
  ) {
    const appRole = storedUser.userRole ?? storedUser.role;
    roles = normalizeRoles(appRole);
  }
  return {
    userId: raw.userId ?? raw.user_id ?? raw.id,
    firstName: raw.firstName ?? raw.first_name ?? '',
    lastName: raw.lastName ?? raw.last_name ?? '',
    email: raw.email ?? raw.Email ?? '',
    mobile: raw.mobile ?? raw.Mobile ?? '',
    aadharNo: raw.aadharNo ?? raw.aadhar_no ?? raw.aadharNumber ?? '',
    status: raw.status ?? raw.Status ?? '',
    roles,
  };
}

function getInitials(userData) {
  const first = String(userData?.firstName || '').trim();
  const last = String(userData?.lastName || '').trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || first.charAt(0).toUpperCase();
  }
  return String(userData?.email || 'U').charAt(0).toUpperCase();
}

function formatRoleLabel(role) {
  return String(role || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isSuperAdminUser(storedUser, userData) {
  if (isEatsAppRootAdmin(storedUser)) return true;
  const storedRole = String(storedUser?.userRole || storedUser?.role || '').toUpperCase();
  if (storedRole === 'ROOT_ADMIN') return true;
  return (userData?.roles || []).some((r) => String(r).toUpperCase() === 'ROOT_ADMIN');
}

const Profile = ({ embedded = false }) => {
  const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
  const userId = storedUser?.userId ?? storedUser?.user_id;

  const [userData, setUserData] = useState(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      setFetching(true);
      try {
        const response = await axiosRestaurantInstance.get(
          RESTAURANT_API_PATHS.restaurantUserById(userId)
        );
        setUserData(normalizeUserProfile(response.data?.user, storedUser));
      } catch (error) {
        console.error('Error fetching user data:', error);
        message.error('Failed to fetch user data.');
      } finally {
        setFetching(false);
      }
    };

    if (userId) fetchUser();
    else setFetching(false);
  }, [userId]);

  const displayName = useMemo(() => {
    if (!userData) return '';
    return `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email || 'User';
  }, [userData]);

  const statusLabel = String(userData?.status || 'ACTIVE').toUpperCase();
  const isActive = statusLabel === 'ACTIVE';
  const canEdit = !isSuperAdminUser(storedUser, userData);

  const startEditing = () => {
    if (!canEdit) return;
    editForm.setFieldsValue(userData);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    editForm.resetFields();
    setIsEditing(false);
  };

  const handlePasswordChange = async (values) => {
    setLoading(true);
    try {
      const verifyResponse = await axiosRestaurantInstance.post(
        RESTAURANT_API_PATHS.restaurantVerifyPassword(userId),
        { password: values.oldPassword }
      );

      if (verifyResponse.status === 200) {
        const updateResponse = await axiosRestaurantInstance.put(
          RESTAURANT_API_PATHS.restaurantUserById(userId),
          { password: values.password }
        );

        if (updateResponse.status === 200) {
          message.success('Password updated successfully');
          setIsPasswordModalOpen(false);
          form.resetFields();
        } else {
          message.error('Failed to update password.');
        }
      } else {
        message.error('Old password is incorrect.');
      }
    } catch (error) {
      console.error('Error during password change:', error);
      message.error(error.response?.data?.error || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileEdit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        mobile: values.mobile,
        aadharNo: values.aadharNo,
      };
      const response = await axiosRestaurantInstance.put(
        RESTAURANT_API_PATHS.restaurantUserById(userId),
        payload
      );

      if (response.status === 200) {
        message.success('Profile updated successfully');
        const updated = normalizeUserProfile(response.data?.user, storedUser) || {
          ...userData,
          ...payload,
        };
        setUserData(updated);
        try {
          const nextStored = {
            ...storedUser,
            userId: updated.userId ?? userId,
            firstName: updated.firstName,
            lastName: updated.lastName,
            email: updated.email,
            mobile: updated.mobile,
            aadharNo: updated.aadharNo,
          };
          localStorage.setItem('user', JSON.stringify(nextStored));
        } catch {
          // ignore storage errors
        }
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      message.error('Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const renderFieldGrid = () => (
    <dl className="maya-profile__field-grid">
      <div className="maya-profile__field">
        <dt>First name</dt>
        <dd>{userData.firstName || '—'}</dd>
      </div>
      <div className="maya-profile__field">
        <dt>Last name</dt>
        <dd>{userData.lastName || '—'}</dd>
      </div>
      <div className="maya-profile__field">
        <dt>Email</dt>
        <dd>{userData.email || '—'}</dd>
      </div>
      <div className="maya-profile__field">
        <dt>Mobile</dt>
        <dd>{formatMobileDisplayOrFallback(userData.mobile, '—')}</dd>
      </div>
      <div className="maya-profile__field">
        <dt>Aadhaar</dt>
        <dd>{formatAadharDisplayOrFallback(userData.aadharNo)}</dd>
      </div>
      <div className="maya-profile__field">
        <dt>User ID</dt>
        <dd>{userData.userId ?? '—'}</dd>
      </div>
    </dl>
  );

  const renderEditForm = () => (
    <Form
      form={editForm}
      layout="vertical"
      onFinish={handleProfileEdit}
      className="maya-profile__edit-form"
      requiredMark={false}
    >
      <div className="maya-profile__edit-grid">
        <Form.Item name="firstName" label="First name" rules={[{ required: true, message: 'Required' }]}>
          <Input size="large" placeholder="First name" />
        </Form.Item>
        <Form.Item name="lastName" label="Last name" rules={[{ required: true, message: 'Required' }]}>
          <Input size="large" placeholder="Last name" />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}
        >
          <Input size="large" placeholder="name@company.com" />
        </Form.Item>
        <Form.Item name="mobile" label="Mobile" rules={[{ required: true, message: 'Required' }]}>
          <Input size="large" placeholder="Mobile number" />
        </Form.Item>
        <Form.Item
          name="aadharNo"
          label="Aadhaar"
          className="maya-profile__edit-span-2"
          rules={[
            { required: true, message: 'Aadhaar number is required' },
            {
              validator: (_, value) => {
                const err = validateAadharNumber(value);
                return err ? Promise.reject(new Error(err)) : Promise.resolve();
              },
            },
          ]}
          normalize={sanitizeAadharInput}
          getValueProps={(value) => ({ value: formatAadharDisplay(value) })}
        >
          <Input size="large" maxLength={14} inputMode="numeric" placeholder="XXXX XXXX XXXX" />
        </Form.Item>
      </div>

      <div className="maya-profile__form-actions">
        <Button type="primary" htmlType="submit" size="large" loading={loading}>
          Save changes
        </Button>
        <Button size="large" onClick={cancelEditing} disabled={loading}>
          Cancel
        </Button>
      </div>
    </Form>
  );

  return (
    <div className={`maya-profile${embedded ? '' : ' maya-profile--standalone'}`}>
      {!embedded ? (
        <button type="button" className="maya-profile__close" onClick={() => navigate(-1)} aria-label="Close">
          <CloseOutlined />
        </button>
      ) : null}

      {fetching ? (
        <div className="maya-profile__loading">
          <Spin size="large" />
        </div>
      ) : !userData ? (
        <div className="maya-profile__empty">Unable to load profile.</div>
      ) : (
        <>
          <section className="maya-profile__hero">
            <div className="maya-profile__avatar" aria-hidden="true">
              {getInitials(userData)}
            </div>
            <div className="maya-profile__hero-meta">
              <h2 className="maya-profile__hero-name">{displayName}</h2>
              <p className="maya-profile__hero-email">{userData.email || '—'}</p>
              <div className="maya-profile__badges">
                <span className={`maya-profile__badge maya-profile__badge--status${isActive ? ' is-active' : ''}`}>
                  {statusLabel || 'UNKNOWN'}
                </span>
                {(userData.roles?.length ? userData.roles : ['No role']).map((role) => (
                  <span key={role} className="maya-profile__badge maya-profile__badge--role">
                    {formatRoleLabel(role)}
                  </span>
                ))}
              </div>
            </div>
          </section>

          <section className="maya-profile__panel">
            <div className="maya-profile__panel-head">
              <div>
                <h3>Personal information</h3>
                <p>Contact and identity details associated with your account.</p>
              </div>
              {isEditing && canEdit ? (
                <span className="maya-profile__editing-pill">Editing</span>
              ) : canEdit ? (
                <Button className="maya-profile__edit-btn" onClick={startEditing}>
                  Edit
                </Button>
              ) : null}
            </div>
            <div className="maya-profile__panel-body">
              {isEditing && canEdit ? renderEditForm() : renderFieldGrid()}
            </div>
          </section>

          {!isEditing || !canEdit ? (
            <section className="maya-profile__panel">
              <div className="maya-profile__panel-head">
                <div>
                  <h3>Security</h3>
                  <p>Keep your account secure with a strong password.</p>
                </div>
              </div>
              <div className="maya-profile__panel-body maya-profile__security-row">
                <div>
                  <strong>Password</strong>
                  <p>Last updated credentials are managed securely. Change anytime.</p>
                </div>
                <Button onClick={() => setIsPasswordModalOpen(true)}>Change password</Button>
              </div>
            </section>
          ) : null}
        </>
      )}

      <Modal
        title={null}
        open={isPasswordModalOpen}
        onCancel={() => {
          setIsPasswordModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
        className="maya-profile-password-modal"
      >
        <Form form={form} layout="vertical" onFinish={handlePasswordChange} requiredMark={false}>
          <Form.Item
            name="oldPassword"
            label="Current password"
            rules={[{ required: true, message: 'Enter current password' }]}
          >
            <Input.Password size="large" />
          </Form.Item>
          <Form.Item
            name="password"
            label="New password"
            rules={[{ required: true, message: 'Enter new password' }]}
          >
            <Input.Password size="large" />
          </Form.Item>
          <div className="maya-profile__form-actions">
            <Button type="primary" htmlType="submit" size="large" loading={loading} block>
              Update password
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Profile;
